import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ArrowUpCircle, History, Info, Landmark, Loader2, RefreshCw, Send, Wallet,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import AccessBlockedNotice from '../../components/AccessBlockedNotice';
import PaymentMethodLogo from '../../components/PaymentMethodLogo';
import RejectReasonNote from '../../components/RejectReasonNote';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client, { createIdempotencyKey } from '../../lib/api';
import { isValidWithdrawalWallet } from '../../lib/cryptoWallet';
import {
  SHOWCASE_UNAVAILABLE_TITLE, isShowcaseMethod, showcaseHasCustomMessage,
  showcaseMessage,
} from '../../lib/showcaseMethod';
import StatusBadge from '../../components/StatusBadge';
import { accountHook } from '../../lib/domHooks';

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR')} ₺`;
const compactIban = (value) => value.replace(/\s/g, '').toUpperCase();

const withdrawStatusMap = {
  onaylandi: { label: 'Ödendi', color: '#22c55e' },
  beklemede: { label: 'İnceleniyor', color: '#f59e0b' },
  reddedildi: { label: 'Reddedildi', color: '#e11d48' },
};

// Hedef hesap kayıtta tam duruyor; listede yalnızca tanımaya yetecek kadarı
// gösterilir (omuz üstünden bakan biri IBAN'ın tamamını görmesin).
const maskTail = (value, head = 0, tail = 4) => {
  const text = String(value || '').trim();
  if (text.length <= head + tail) return text;
  return `${text.slice(0, head)}••••${text.slice(-tail)}`;
};

const payoutSummary = (transaction) => {
  if (transaction.payout_type === 'crypto') {
    const network = transaction.payout_crypto_network;
    return `${network ? `${network} · ` : ''}${maskTail(transaction.payout_wallet_address, 6)}`;
  }
  // Sağlayıcı cüzdanına çekim: hedef IBAN değil, cüzdan numarasıdır.
  if (transaction.payout_type === 'wallet') {
    return maskTail(transaction.payout_wallet_address, 3);
  }
  const bank = transaction.payout_bank_name;
  const iban = maskTail(transaction.payout_iban, 6);
  return [bank, iban].filter(Boolean).join(' · ') || transaction.note || '—';
};

const Withdraw = () => {
  const { balance, setBalance, user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(
    searchParams.get('view') === 'history' ? 'history' : 'new',
  );
  // Tutar alanı boş başlar: hazır bir değer minimum çekim tutarının altında
  // kalabildiği için oyuncu farkında olmadan geçersiz talep gönderiyordu.
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [iban, setIban] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [methodId, setMethodId] = useState('');
  // Göstermelik yöntemin uyarısı formda kalıcı durur: toast birkaç saniyede
  // kaybolur ve oyuncu düğmeye tekrar basıp "çalışmıyor" diye bildirirdi.
  const [showcaseNotice, setShowcaseNotice] = useState(false);
  const [methods, setMethods] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [config, setConfig] = useState({
    min_amount: 100,
    max_amount: 100000,
  });
  const requestKey = useRef(null);

  const loadWithdrawals = useCallback(async (showLoading = true) => {
    if (showLoading) setHistoryLoading(true);
    try {
      // Süzgeç sunucudadır; bkz. Deposit.jsx — istemcide ayıklamak aktif
      // oyuncuda geçmişi boş bırakıyordu.
      const { data } = await client.get('/finance/transactions', {
        params: { limit: 100, type: 'withdraw' },
      });
      setWithdrawHistory(data.items || []);
    } catch (error) {
      if (showLoading) {
        toast({
          title: 'Çekimler alınamadı',
          description: error.response?.data?.detail || 'İşlem geçmişi yüklenemedi.',
        });
      }
    } finally {
      if (showLoading) setHistoryLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  // Talep yönetici onayına düştüğü için durum sayfa açıkken değişebilir.
  useEffect(() => {
    if (view !== 'history') return undefined;
    const timer = setInterval(() => loadWithdrawals(false), 15000);
    return () => clearInterval(timer);
  }, [loadWithdrawals, view]);

  const changeView = (nextView) => {
    setView(nextView);
    if (nextView === 'history') {
      setSearchParams({ view: 'history' }, { replace: true });
      loadWithdrawals();
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    Promise.all([
      client.get('/finance/withdraw-config'),
      client.get('/payments/methods', {
        params: { direction: 'withdrawal' },
        headers: { 'Cache-Control': 'no-cache' },
      }),
    ])
      .then(([configResponse, methodsResponse]) => {
        const availableMethods = (methodsResponse.data.items || [])
          .filter((method) => (
            ['bank', 'crypto', 'wallet'].includes(method.method_type)));
        setConfig(configResponse.data);
        setMethods(availableMethods);
        setMethodId((current) => (
          availableMethods.some((method) => method.id === current)
            ? current
            : availableMethods[0]?.id || ''
        ));
      })
      .catch(() => setMethods([]))
      .finally(() => setLoading(false));
  }, []);

  const selectedMethod = useMemo(
    () => methods.find((method) => method.id === methodId) || null,
    [methodId, methods],
  );
  const selectedIsCrypto = selectedMethod?.method_type === 'crypto';
  // Sağlayıcı cüzdanına çekim (Payurus/CepPay): IBAN yerine cüzdan numarası
  // istenir, banka adı sorulmaz.
  const selectedIsWallet = selectedMethod?.method_type === 'wallet';
  const selectedIsPayurus = selectedMethod?.provider === 'payurus';
  // UltraPay cüzdanları (JoyPayz, Yesod) yalnızca rakamdan oluşmaz —
  // Yesod numarası `YSD-…` önekiyle gelir. Biçim bu yüzden sağlayıcıya göre
  // ayrışır; tek bir kalıba zorlamak geçerli numarayı yazdırmazdı.
  const selectedIsUltraPay = selectedMethod?.provider === 'ultrapay';
  // Sabit uzunlukta rakam isteyen cüzdan (JoyPayz: 10 hane). Uzunluk
  // sağlayıcının yöntem satırından gelir; eksik/fazla haneli numarayla talep
  // açılmaz, çünkü sağlayıcı onu yönetici onayından **sonra** reddeder.
  const walletDigits = selectedIsWallet
    ? Number(selectedMethod?.wallet_digits) || 0
    : 0;
  const walletPattern = walletDigits
    ? new RegExp(`^\\d{${walletDigits}}$`)
    : selectedIsUltraPay
      ? /^[A-Za-z0-9][A-Za-z0-9._-]{4,31}$/
      : /^\d{6,24}$/;
  const walletIncomplete = Boolean(walletDigits)
    && !walletPattern.test(walletAddress.trim());
  // Link Havale'de cüzdan yöntemi Papara'dır (yalnızca rakam); havale IBAN,
  // kripto ise ağ + adres ister.
  const selectedIsLinkHavale = selectedMethod?.provider === 'linkhavale';
  // VEVO PAY çekimi doğrudan IBAN'a yapılır; hosted akış kullanılmaz —
  // oyuncunun IBAN'ını sağlayıcının sayfasında yeniden yazdırmak, kendi
  // adına kayıtlı hesap kuralını devre dışı bırakırdı.
  const selectedIsVevoPay = selectedMethod?.provider === 'vevopay';
  // HızlıcaParalar çekimi doğrudan IBAN'a ya da TRC20 cüzdan adresine
  // yapılır; hedef banka sağlayıcının **kendi kodudur** ve serbest bir ad
  // sağlayıcı tarafında reddedilir, bu yüzden banka adı sorulmaya devam eder.
  const selectedIsHizlicaParalar =
    selectedMethod?.provider === 'hizlicaparalar';
  // Sağlayıcının tanıdığı banka listesi. Geldiğinde alan serbest metin
  // olmaktan çıkıp seçime döner: yazılan ad sağlayıcıda karşılığı yoksa
  // çekim "Banka adı geçersiz" ile reddedilir ve bu, yönetici talebi
  // onayladıktan **sonra** olur — oyuncu sebebini hiç göremez.
  const bankOptions = Array.isArray(selectedMethod?.banks)
    ? selectedMethod.banks.filter((bank) => bank && bank.code)
    : [];
  // Hızlı Havale çekimi doğrudan IBAN'a yapılır; hedef banka **serbest bir
  // addır** (sağlayıcı ``recipientBank``'i isteğe bağlı sayar ve kendi banka
  // kodlarını istemez) — HızlıcaParalar'ın kod listesiyle karıştırmayın.
  const selectedIsHizliHavale = selectedMethod?.provider === 'hizlihavale';
  const selectedIsWizkasa = selectedMethod?.provider === 'wizkasa';
  const selectedIsHemenSystem = selectedMethod?.provider === 'hemensystem';
  const selectedIsHera = selectedMethod?.provider === 'hera';
  const minimum = Math.max(
    Number(config.min_amount || 0),
    Number(selectedMethod?.min_amount || 0),
  );
  const maximum = Math.min(
    Number(config.max_amount || 100000),
    Number(selectedMethod?.max_amount || config.max_amount || 100000),
  );
  const pendingWithdrawCount = withdrawHistory.filter(
    (transaction) => transaction.status === 'beklemede',
  ).length;

  const changeRequestValue = (setter) => (event) => {
    requestKey.current = null;
    setter(event.target.value);
  };

  const submit = async () => {
    const requestedAmount = Number(amount);
    const normalizedIban = compactIban(iban);
    const normalizedWallet = walletAddress.trim();
    if (!selectedMethod) {
      toast({ title: 'Para çekme yöntemi seçin' });
      return;
    }
    // Göstermelik firmanın arkasında hesap yoktur; talep açılsaydı tutar
    // bakiyeden düşer ve ödenemeyecek bir kayıt beklerdi. Uyarı hesap/tutar
    // doğrulamalarından **önce** çıkar: yöntemin kendisi kapalıdır, oyuncuya
    // önce IBAN'ını düzeltmesini söylemek yanlış yere bakmasına yol açardı.
    if (isShowcaseMethod(selectedMethod)) {
      setShowcaseNotice(true);
      toast({
        title: SHOWCASE_UNAVAILABLE_TITLE,
        description: showcaseMessage(selectedMethod),
      });
      return;
    }
    if (selectedIsWallet) {
      // Biçim sunucuda da doğrulanır; buradaki kontrol oyuncunun forma kadar
      // gelip 400 yemesini önler.
      if (!walletPattern.test(normalizedWallet)) {
        toast({
          title: 'Geçerli bir cüzdan numarası girin',
          description: walletDigits
            ? `Cüzdan numarası ${walletDigits} haneli olmalıdır.`
            : selectedIsUltraPay
              ? 'Cüzdan numarasını yöntemin verdiği biçimde girin.'
              : 'Cüzdan numarası yalnızca rakamlardan oluşur.',
        });
        return;
      }
    } else if (selectedIsCrypto) {
      if (!isValidWithdrawalWallet(normalizedWallet, selectedMethod.network)) {
        toast({
          title: 'Geçerli bir kripto cüzdan adresi girin',
          description: selectedMethod.network
            ? `${selectedMethod.network} ağına uygun bir adres kullanın.`
            : undefined,
        });
        return;
      }
    } else {
      if (!bankName.trim()) {
        toast({ title: 'Banka adını girin' });
        return;
      }
      if (
        normalizedIban.length !== 26
        || !normalizedIban.startsWith('TR')
        || !/^[A-Z0-9]+$/.test(normalizedIban)
      ) {
        toast({ title: 'Geçerli bir IBAN girin' });
        return;
      }
    }
    if (
      !Number.isFinite(requestedAmount)
      || requestedAmount < minimum
      || requestedAmount > maximum
    ) {
      toast({
        title: 'Geçersiz tutar',
        description: `${money(minimum)} ile ${money(maximum)} arasında bir tutar girin.`,
      });
      return;
    }
    // Bazı sağlayıcılar (UltraPay) kuruşlu tutar kabul etmez; bayrağı uç
    // bildirir. Kontrol sunucuda da vardır; buradaki, oyuncunun forma kadar
    // gelip 400 yemesini önler.
    if (selectedMethod.integer_amount_only
      && !Number.isInteger(requestedAmount)) {
      toast({
        title: 'Tutar tam sayı olmalıdır',
        description: `${selectedMethod.name} ile kuruşlu tutar gönderilemez.`,
      });
      return;
    }
    if (requestedAmount > balance) {
      toast({ title: 'Yetersiz Bakiye' });
      return;
    }
    setBusy(true);
    try {
      requestKey.current ||= createIdempotencyKey();
      const { data } = await client.post(
        '/finance/withdraw',
        {
          amount: requestedAmount,
          method_id: selectedMethod.id,
          bank_name: (
            selectedIsCrypto || selectedIsWallet ? undefined : bankName.trim()),
          iban: (
            selectedIsCrypto || selectedIsWallet ? undefined : normalizedIban),
          wallet_address: (
            selectedIsCrypto || selectedIsWallet ? normalizedWallet : undefined),
        },
        { headers: { 'Idempotency-Key': requestKey.current } },
      );
      requestKey.current = null;
      setBalance(data.balance);
      setAmount('');
      toast({
        title: 'Çekim Talebi Alındı',
        description: `${money(requestedAmount)} ${selectedMethod.name} ile belirttiğiniz ${selectedIsCrypto || selectedIsWallet ? 'cüzdana' : "IBAN'a"} gönderilmek üzere incelemeye alındı.`,
      });
      // Talep alındıktan sonra oyuncu doğrudan durumu görebildiği listeye
      // düşer; formda kalıp aynı talebi tekrar göndermeye çalışmasın.
      changeView('history');
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Çekim başarısız',
      });
    } finally {
      setBusy(false);
    }
  };

  if (user?.withdraw_blocked) {
    return (
      <AccessBlockedNotice
        title="Para çekme işleminiz kısıtlanmıştır"
        description={'Hesabınız yönetici tarafından para çekme işlemine '
          + 'kapatılmıştır. Bu sayfayı görüntüleyebilirsiniz ancak yeni bir '
          + 'çekim talebi oluşturamazsınız.'}
      />
    );
  }

  return (
    <div {...accountHook('panel')}>
      <div className="mb-5 flex flex-wrap items-center gap-3" {...accountHook('page-header')}>
        <div className="account-icon-soft w-11 h-11 rounded-xl flex items-center justify-center">
          <ArrowUpCircle size={22} className="text-[var(--pusu-gold)]" />
        </div>
        <div>
          <h1 className="text-2xl font-black gold-text" {...accountHook('title')}>Para Çek</h1>
          <p className="text-sm text-gray-400" {...accountHook('subtitle')}>
            Çekim yöntemini seçin ve ödemenin gönderileceği hesabı girin.
          </p>
        </div>
        <div className="ml-auto text-right" {...accountHook('balance')}>
          <div className="text-xs text-gray-400">Mevcut Bakiye</div>
          <div className="font-black gold-text text-lg">
            {Number(balance).toFixed(2)} ₺
          </div>
        </div>
      </div>

      <div className="pusu-panel p-2 mb-5 inline-flex flex-wrap gap-2" {...accountHook('tabs')}>
        <button
          type="button"
          {...accountHook('tab')}
          className={`account-tab px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            view === 'new' ? 'gold-btn' : 'provider-chip'
          }`}
          onClick={() => changeView('new')}
        >
          <ArrowUpCircle size={16} aria-hidden="true" />
          Yeni Çekim
        </button>
        <button
          type="button"
          {...accountHook('tab')}
          className={`account-tab px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            view === 'history' ? 'gold-btn' : 'provider-chip'
          }`}
          onClick={() => changeView('history')}
        >
          <History size={16} aria-hidden="true" />
          Çekimlerim
          {pendingWithdrawCount > 0 && (
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-black flex items-center justify-center">
              {pendingWithdrawCount}
            </span>
          )}
        </button>
      </div>

      {view === 'history' && (
        <div {...accountHook('list')}>
          <div className="flex items-center gap-3 mb-4" {...accountHook('header')}>
            <div>
              <h2 className="text-lg font-bold" {...accountHook('title')}>Çekimlerim</h2>
              <p className="text-xs text-gray-400">
                Çekim taleplerinizin güncel durumunu buradan takip edebilirsiniz.
              </p>
            </div>
            <button
              type="button"
              className="provider-chip ml-auto flex items-center gap-2"
              onClick={() => loadWithdrawals()}
              disabled={historyLoading}
            >
              <RefreshCw
                size={14}
                className={historyLoading ? 'animate-spin' : ''}
                aria-hidden="true"
              />
              Yenile
            </button>
          </div>

          {historyLoading ? (
            <div className="pusu-panel py-12 text-center text-sm text-gray-400" {...accountHook('loading')}>
              <Loader2 size={18} className="inline animate-spin mr-2" />
              Çekimler yükleniyor...
            </div>
          ) : withdrawHistory.length === 0 ? (
            <div className="pusu-panel py-12 px-5 text-center" {...accountHook('empty')}>
              <History size={28} className="mx-auto mb-3 text-gray-500" />
              <div className="font-semibold">Henüz çekim talebiniz bulunmuyor</div>
              <p className="text-xs text-gray-400 mt-1">
                Yeni Çekim sekmesinden ilk talebinizi oluşturabilirsiniz.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {withdrawHistory.map((transaction) => {
                const status = withdrawStatusMap[transaction.status]
                  || withdrawStatusMap.beklemede;
                const method = methods.find(
                  (item) => item.id === transaction.payment_method_id,
                ) || { name: transaction.method };
                return (
                  <div key={transaction.id} className="pusu-panel p-4 sm:p-5" {...accountHook('row')}>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0 lg:w-[28%]">
                        <PaymentMethodLogo method={method} size={44} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {transaction.method || method.name || 'Çekim'}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            İşlem no: {transaction.id}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Tutar</div>
                          <div className="font-bold text-rose-400">
                            {Math.abs(Number(transaction.amount)).toLocaleString('tr-TR')} ₺
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] text-gray-500 uppercase">
                            {['crypto', 'wallet'].includes(transaction.payout_type)
                              ? 'Cüzdan'
                              : 'Hesap'}
                          </div>
                          <div className="text-xs text-gray-300 truncate">
                            {payoutSummary(transaction)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Tarih</div>
                          <div className="text-xs text-gray-300">
                            {new Date(transaction.created_at).toLocaleString('tr-TR')}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Durum</div>
                          <StatusBadge
                            color={status.color}
                            status={transaction.status}
                            className="inline-block mt-0.5 px-2 py-1 rounded text-[10px] font-bold"
                          >
                            {status.label}
                          </StatusBadge>
                        </div>
                      </div>
                    </div>
                    <RejectReasonNote transaction={transaction} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'new' && (
      <>
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">
        Çekim yöntemini seçin
      </div>
      {methods.length > 0 ? (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" {...accountHook('methods')}>
          {methods.map((method) => {
            const selected = method.id === methodId;
            const methodMinimum = Math.max(
              Number(config.min_amount || 0),
              Number(method.min_amount || 0),
            );
            return (
              <button
                key={method.id}
                type="button"
                data-method-id={method.id}
                {...accountHook('method')}
                onClick={() => {
                  requestKey.current = null;
                  setShowcaseNotice(false);
                  setMethodId(method.id);
                }}
                className={`group relative min-h-48 rounded-2xl border p-5 text-center transition ${
                  selected
                    ? 'border-[var(--pusu-gold)] bg-[var(--pusu-gold)]/[0.08] shadow-[0_0_0_1px_rgba(var(--pusu-gold-rgb),0.35)]'
                    : 'border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.055]'
                }`}
              >
                {selected && (
                  <span className="absolute right-3 top-3 rounded-full bg-[var(--pusu-gold)]/15 p-1 text-[var(--pusu-gold)]">
                    <ArrowUpCircle size={17} />
                  </span>
                )}
                <div className="mb-4 flex justify-center">
                  <PaymentMethodLogo method={method} size={76} />
                </div>
                <div className="text-base font-black text-white">
                  {method.name}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {method.method_type === 'crypto' && method.network
                    ? `${method.network} · `
                    : ''}
                  Min: <span className="font-bold text-gray-200">{money(methodMinimum)}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200" {...accountHook('empty')}>
          {loading
            ? 'Para çekme yöntemleri yükleniyor…'
            : 'Şu anda aktif bir para çekme yöntemi bulunmuyor.'}
        </div>
      )}

      {selectedMethod && (
        <div className="pusu-panel w-full p-5 sm:p-7" {...accountHook('form')}>
          <div className="mb-5 flex items-center gap-2 text-xl font-black text-white" {...accountHook('title')}>
            <ArrowUpCircle size={22} className="text-[var(--pusu-gold)]" />
            {selectedMethod.name} ile Çekim
          </div>

          <div className="mb-6 flex gap-3 rounded-xl border border-white/10 border-l-blue-500 bg-black/20 p-4" {...accountHook('notice')}>
            <Info size={19} className="mt-0.5 shrink-0 text-blue-400" />
            <div>
              <div className="mb-1 text-sm font-bold text-white">Talimatlar</div>
              <div className="text-sm leading-relaxed text-gray-400">
                {selectedIsPayurus
                  ? 'Ödemenin gönderileceği kendi CepPay cüzdan numaranızı girin. Cüzdan hesabınızın adı, sitedeki kayıtlı ad soyadınızla aynı olmalıdır. Talebiniz onaylandıktan sonra Payurus üzerinden işleme alınacaktır.'
                  : selectedIsLinkHavale
                  ? (selectedIsCrypto
                    ? `Çekimin gönderileceği kendi ${selectedMethod.network || 'kripto'} cüzdan adresinizi girin. Ağ ile adres uyuşmazsa gönderim geri alınamaz.`
                    : selectedIsWallet
                    ? 'Ödemenin gönderileceği kendi Papara hesap numaranızı girin. Hesabın adı, sitedeki kayıtlı ad soyadınızla aynı olmalıdır.'
                    : 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı ve hedef bankayı girin. Talebiniz onaylandıktan sonra Link Havale üzerinden işleme alınacaktır.')
                  : selectedIsUltraPay
                  ? (selectedIsCrypto
                    ? `Çekimin gönderileceği kendi ${selectedMethod.network || 'kripto'} cüzdan adresinizi girin. Ağ ile adres uyuşmazsa gönderim geri alınamaz. Tutar tam sayı olmalıdır.`
                    : selectedIsWallet
                    ? 'Ödemenin gönderileceği kendi cüzdan numaranızı girin. Cüzdan hesabınızın adı, sitedeki kayıtlı ad soyadınızla aynı olmalıdır. Tutar tam sayı olmalıdır.'
                    : 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı ve hedef bankayı girin. Talebiniz onaylandıktan sonra UltraPay üzerinden işleme alınacaktır. Tutar tam sayı olmalıdır.')
                  : selectedIsVevoPay
                  ? 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı ve hedef bankayı girin. Talebiniz onaylandıktan sonra VEVO PAY üzerinden işleme alınacaktır.'
                  : selectedIsHizlicaParalar
                  ? (selectedIsCrypto
                    ? `Çekimin gönderileceği kendi ${selectedMethod.network || 'TRC20'} cüzdan adresinizi girin. Ağ ile adres uyuşmazsa gönderim geri alınamaz.`
                    : 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı ve hedef bankayı girin. Talebiniz onaylandıktan sonra HızlıcaParalar üzerinden işleme alınacaktır.')
                  : selectedIsHizliHavale
                  ? 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı ve hedef bankayı girin. Talebiniz onaylandıktan sonra Hızlı Havale üzerinden işleme alınacaktır.'
                  : selectedIsHemenSystem
                  ? 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı girin. Talebiniz onaylandıktan sonra Hemen Havale üzerinden işleme alınacaktır.'
                  : selectedIsHera
                  ? 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı girin. Hedef banka IBAN’dan otomatik çözülür; talebiniz onaylandıktan sonra Hera üzerinden işleme alınacaktır.'
                  : selectedIsWizkasa
                  ? (selectedIsCrypto
                    ? 'Çekimin gönderileceği kendi kripto cüzdan adresinizi girin. Talebiniz Wizkasa üzerinden işleme alınacaktır.'
                    : 'Çekimin gönderileceği, kendi adınıza kayıtlı IBAN’ı girin. Talebiniz Wizkasa üzerinden işleme alınacaktır.')
                  : selectedMethod.instructions || (selectedIsCrypto
                    ? `${selectedMethod.name} (${selectedMethod.network || 'Kripto ağı'}) ile çekim yapmak için kendi cüzdan adresinizi girin.`
                    : `${selectedMethod.name} ile çekim yapmak için kendi adınıza kayıtlı IBAN'ı girin.`)}
              </div>
            </div>
          </div>

          {!selectedIsCrypto && !selectedIsWallet && (
            <>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-400">
                Banka Adı
              </label>
              <div className="relative mb-4" {...accountHook('field', 'bank')}>
                <Landmark
                  size={18}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]"
                />
                {bankOptions.length ? (
                  <select
                    value={bankName}
                    onChange={changeRequestValue(setBankName)}
                    className="pusu-input py-4 pl-12"
                  >
                    <option value="">Banka seçin...</option>
                    {bankOptions.map((bank) => (
                      <option key={bank.code} value={bank.name || bank.code}>
                        {bank.name || bank.code}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={bankName}
                    onChange={changeRequestValue(setBankName)}
                    maxLength={128}
                    autoComplete="organization"
                    placeholder="Çekim yapılacak banka..."
                    className="pusu-input py-4 pl-12"
                  />
                )}
              </div>
            </>
          )}

          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-400">
            Çekim Tutarı (₺)
          </label>
          <div className="mb-2 flex max-w-xl overflow-hidden rounded-xl border border-white/10 bg-black/20 focus-within:border-[var(--pusu-gold)]/60" {...accountHook('field', 'amount')}>
            <div className="flex w-12 shrink-0 items-center justify-center border-r border-white/10 font-black text-white">
              ₺
            </div>
            <input
              type="number"
              min={minimum}
              max={maximum}
              step="0.01"
              value={amount}
              onChange={changeRequestValue(setAmount)}
              placeholder={`Min: ${money(minimum)}`}
              className="min-w-0 flex-1 bg-transparent px-4 py-4 text-white outline-none"
            />
          </div>
          <div className="mb-6 flex max-w-xl justify-between text-xs text-gray-500">
            <span>Minimum: {money(minimum)}</span>
            <span>Maksimum: {money(maximum)}</span>
          </div>

          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-400">
            {selectedIsWallet
              ? 'Cüzdan Numarası'
              : selectedIsCrypto ? 'Cüzdan Adresi' : 'IBAN'}
          </label>
          <div className="relative mb-6" {...accountHook('field', 'account')}>
            {selectedIsCrypto || selectedIsWallet ? (
              <Wallet
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]"
              />
            ) : (
              <Landmark
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]"
              />
            )}
            {selectedIsWallet ? (
              <input
                type="text"
                inputMode={
                  selectedIsUltraPay && !walletDigits ? 'text' : 'numeric'
                }
                value={walletAddress}
                onChange={changeRequestValue(
                  (value) => setWalletAddress(
                    selectedIsUltraPay && !walletDigits
                      // Yesod cüzdanı `YSD-…` önekiyle gelir; rakama zorlamak
                      // geçerli numarayı yazdırmazdı.
                      ? value.replace(/[^A-Za-z0-9._-]/g, '')
                      : value.replace(/\D/g, ''),
                  ))}
                maxLength={
                  walletDigits || (selectedIsUltraPay ? 32 : 24)
                }
                aria-invalid={walletIncomplete && walletAddress ? true : undefined}
                aria-describedby={walletDigits ? 'withdraw-wallet-hint' : undefined}
                autoComplete="off"
                placeholder={
                  selectedIsUltraPay
                    ? `${selectedMethod.name} cüzdan numaranızı girin...`
                    : selectedIsLinkHavale
                      ? 'Papara hesap numaranızı girin...'
                      : 'CepPay cüzdan numaranızı girin...'
                }
                className="pusu-input py-4 pl-12"
              />
            ) : selectedIsCrypto ? (
              <input
                type="text"
                value={walletAddress}
                onChange={changeRequestValue(setWalletAddress)}
                maxLength={255}
                autoComplete="off"
                placeholder={`${selectedMethod.network || 'Kripto'} cüzdan adresinizi girin...`}
                className="pusu-input py-4 pl-12"
              />
            ) : (
              <input
                type="text"
                value={iban}
                onChange={changeRequestValue((value) => setIban(value.toUpperCase()))}
                maxLength={32}
                autoComplete="off"
                placeholder="IBAN girin..."
                className="pusu-input py-4 pl-12"
              />
            )}
          </div>

          {walletDigits > 0 && (
            <p
              id="withdraw-wallet-hint"
              className="-mt-4 mb-6 text-xs text-gray-500"
            >
              Cüzdan numarası {walletDigits} haneli olmalıdır
              {' '}({walletAddress.length}/{walletDigits}).
            </p>
          )}

          {showcaseNotice && (
            <div
              role="alert"
              className="account-warning mb-4 p-3 text-xs"
              {...accountHook('notice')}
            >
              {!showcaseHasCustomMessage(selectedMethod) && (
                <div className="font-bold mb-1">
                  {SHOWCASE_UNAVAILABLE_TITLE}
                </div>
              )}
              {showcaseMessage(selectedMethod)}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={busy || loading || walletIncomplete}
            {...accountHook('submit')}
            className="gold-btn flex w-full items-center justify-center gap-2 rounded-xl py-4 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <Send size={17} />
            )}
            Çekim Talebi Oluştur
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Withdraw;
